import React from 'react';
import { Document, Page } from 'react-pdf';
import { HandDrawnAnnotations } from '../../HandDrawnAnnotations';

export interface PDFCanvasStageProps {
  /** PDF document URL */
  fileUrl: string;
  /** Ref for the annotation canvas */
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /** Current page number */
  pageNumber: number;
  /** Pan/scale transform */
  panX: number;
  panY: number;
  scale: number;
  isPanning: boolean;
  /** PDF render scale and DPI */
  pdfRenderScale: number;
  devicePixelRatio: number;
  /** Page rotations keyed by page number */
  pageRotations: Record<number, number>;
  /** Document load success callback */
  onDocumentLoadSuccess: (pdf: any) => void;
  /** Page load success callback */
  onPageLoadSuccess: (page: any) => void;
  /** Phase 1 render complete callback */
  onPhase1Complete: () => void;
  /** Mouse/interaction handlers */
  onMouseDown: React.MouseEventHandler<HTMLDivElement>;
  onMouseMove: React.MouseEventHandler<HTMLDivElement>;
  onMouseUp: React.MouseEventHandler<HTMLDivElement>;
  onViewportMouseLeave: () => void;
  onContextMenu: React.MouseEventHandler<HTMLDivElement>;
  /** Canvas drop handler */
  onCanvasDrop: (e: React.DragEvent<HTMLCanvasElement>) => void;
  /** Cursor style for the canvas */
  canvasCursor: string;
  /** Show "Enhancing resolution…" overlay */
  isUpgradingRes: boolean;
  /** Props for HandDrawnAnnotations */
  handDrawnProps: React.ComponentProps<typeof HandDrawnAnnotations>;
  /** Children rendered at end of viewport (e.g. snapshot action buttons) */
  children?: React.ReactNode;
}

export const PDFCanvasStage: React.FC<PDFCanvasStageProps> = ({
  fileUrl,
  canvasRef,
  pageNumber,
  panX,
  panY,
  scale,
  isPanning,
  pdfRenderScale,
  devicePixelRatio,
  pageRotations,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  onPhase1Complete,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onViewportMouseLeave,
  onContextMenu,
  onCanvasDrop,
  canvasCursor,
  isUpgradingRes,
  handDrawnProps,
  children,
}) => {
  return (
    <div
      className="pdf-viewport"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--bg-tertiary)',
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onViewportMouseLeave}
      onContextMenu={onContextMenu}
    >
      <div
        className="pdf-stage"
        style={{
          transform: `translate(${panX}px, ${panY}px) scale(${scale})`,
          transformOrigin: '0 0',
          position: 'absolute',
          transition: isPanning ? 'none' : 'transform 0.1s ease-out',
        }}
      >
        <div
          style={{
            boxShadow: '0 10px 30px rgba(0,0,0,0.15), 0 1px 8px rgba(0,0,0,0.1)',
            background: 'var(--bg-primary)',
            position: 'relative',
            display: 'inline-block',
          }}
        >
          <Document
            file={fileUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<div />}
            error={
              <div className="pdf-error">
                <p>Failed to load PDF</p>
              </div>
            }
          >
            <Page
              pageNumber={pageNumber}
              onLoadSuccess={onPageLoadSuccess}
              onRenderSuccess={onPhase1Complete}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              scale={pdfRenderScale}
              devicePixelRatio={devicePixelRatio}
              rotate={pageRotations[pageNumber] || 0}
            />
          </Document>

          <canvas
            ref={canvasRef}
            className="annotation-canvas"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onCanvasDrop}
            style={{ cursor: canvasCursor }}
          />

          {isUpgradingRes && (
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                background: 'rgba(0,0,0,0.5)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 11,
                zIndex: 20,
                pointerEvents: 'none',
              }}
            >
              Enhancing resolution…
            </div>
          )}

          <HandDrawnAnnotations {...handDrawnProps} />
        </div>
      </div>
      {children}
    </div>
  );
};
